/**
 * Personalized Order Form - 5-Card Sliding Window System
 * Handles dynamic card rendering, navigation, and form submission
 */

class PersonalizedOrderForm {
  constructor(formElement) {
    this.container = formElement.closest('.personalized-order-form');
    this.cardsContainer = this.container.querySelector('.cards-container');
    this.form = formElement;
    this.currentStep = 1;
    this.totalSteps = 10;
    this.isSubmitting = false;
    
    // Store global reference for onclick handlers
    window.personalizedOrderForm = this;
    
    // API configuration - will be set per request
    this.apiEndpoint = null;
    
    // ImgBB API configuration
    this.imgbbApiKey = 'cae5f081ad906dd62783695c83dd4cad'; // Replace with your ImgBB API key
    this.uploadedImageUrl = null; // Store the uploaded image URL
    
    // Step configuration (now titles/descriptions come from Shopify settings in HTML)
    this.stepConfig = [
      { title: 'Book Format', description: 'Choose PDF, Hardcover, or Paper Back' },
      { title: 'The Basics', description: 'Names, gender and age group' },
      { title: 'Appearance Details', description: 'Tell us how your child looks' },
      { title: 'Favorites', description: 'Favorite things and hobbies' },
      { title: 'Inner Strengths', description: 'Superpowers and special abilities' },
      { title: 'The Occasion', description: 'What\'s the special occasion?' },
      { title: 'Values & Life Lessons', description: 'Choose important values and life lessons' },
      { title: 'Exploration & Joy', description: 'Adventures and achievements' },
      { title: 'Picture Upload', description: 'Upload your child\'s picture' },
      { title: 'Dedication & Gifting', description: 'Add a special dedication message' }
    ];
    
    this.init();
  }

  init() {
    this.renderCards();
    this.bindEvents();
    this.initializeValidation();
    this.initializeTagSelection();
    this.initializeColorSelection();
    this.initializeCircleSelection();
    this.initializeCharacterCount();
    this.setupVariantSelection();
    this.setupInnerStrengthsLimit();
    this.initializeFileUpload();
    this.initializeAutoSave();
    this.loadSavedFormData();
    
    // Initial entrance animation
    setTimeout(() => {
      this.animateCardsIn();
    }, 100);
  }

  animateCardsIn() {
    const cards = Array.from(this.cardsContainer.querySelectorAll('.step-card'));
    cards.forEach((card, index) => {
      card.style.transform = 'translateY(30px)';
      card.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      
      setTimeout(() => {
        card.style.transform = 'translateY(0)';
      }, index * 120);
    });
  }

  getNextCardContinueHint() {
    return 'Click here to continue';
  }

  addMobileStepContinueAction(clonedStep, stepNum) {
    if (stepNum >= this.totalSteps) return;

    const continueAction = document.createElement('button');
    continueAction.type = 'button';
    continueAction.className = 'mobile-step-continue';
    continueAction.textContent = 'Tap here to continue';
    continueAction.setAttribute('aria-label', 'Continue to the next step');

    const fieldsContainer = clonedStep.querySelector('.form-fields');
    if (fieldsContainer) {
      fieldsContainer.appendChild(continueAction);
    } else {
      clonedStep.appendChild(continueAction);
    }
  }

  renderCards() {
    // Preserve data elements and clear only the step cards
    const dataElements = this.cardsContainer.querySelectorAll('[data-step-1-image], [data-step-2-image], [data-step-3-image], [data-step-4-image], [data-step-5-image], [data-step-6-image], [data-step-7-image], [data-step-8-image], [data-step-9-image], [data-step-10-image], [data-step-11-image], [data-step-12-image], [data-step-13-image], [data-step-14-image]');
    const hiddenForm = this.cardsContainer.querySelector('.multi-step-form');
    
    // Clear existing cards but preserve data elements and form
    this.cardsContainer.innerHTML = '';
    
    // Re-add the preserved elements
    dataElements.forEach(element => this.cardsContainer.appendChild(element));
    if (hiddenForm) this.cardsContainer.appendChild(hiddenForm);
    
    // Calculate which cards to show (always 5 cards)
    const cardSteps = this.getVisibleCardSteps();
    
    cardSteps.forEach((stepNum, index) => {
      const card = this.createCard(stepNum, index);
      
      // For mobile vertical layout, ensure content card positioning
      const isMobile = window.innerWidth <= 768;
      const isCurrentStep = stepNum === this.currentStep;
      
      if (isMobile && isCurrentStep) {
        // Set explicit order for content card to appear in middle on mobile
        card.style.order = '2';
      } else if (isMobile) {
        // Navigation cards get order based on their position relative to current step
        if (stepNum < this.currentStep) {
          card.style.order = '1'; // Before current step - appears at top
        } else {
          card.style.order = '3'; // After current step - appears at bottom
        }
      }
      
      this.cardsContainer.appendChild(card);
    });

    // Update tag states after rendering new cards
    setTimeout(() => {
      this.updateAllTagStates();
    }, 100);
  }

  getVisibleCardSteps() {
    // Show 3 cards on mobile, 5 on desktop
    const isMobile = window.innerWidth <= 768;
    const numCards = isMobile ? 3 : 5;
    let start, end;
    
    if (isMobile) {
      // Mobile: 3 cards with current step in middle when possible
      if (this.currentStep <= 1) {
        // Early steps: show steps 1-3
        start = 1;
        end = 3;
      } else if (this.currentStep >= this.totalSteps) {
        // Late steps: show last 3 steps
        start = this.totalSteps - 2;
        end = this.totalSteps;
      } else {
        // Middle steps: center current step
        start = this.currentStep - 1;
        end = this.currentStep + 1;
      }
    } else {
      // Desktop: 5 cards with current step somewhere in the middle
      if (this.currentStep <= 2) {
        // Early steps: show steps 1-5
        start = 1;
        end = 5;
      } else if (this.currentStep >= this.totalSteps - 1) {
        // Late steps: show last 5 steps
        start = this.totalSteps - 4;
        end = this.totalSteps;
      } else {
        // Middle steps: center current step
        start = this.currentStep - 2;
        end = this.currentStep + 2;
      }
    }
    
    const steps = [];
    for (let i = start; i <= end; i++) {
      steps.push(i);
    }
    
    return steps;
  }

  createCard(stepNum, cardIndex) {
    const card = document.createElement('div');
    const isCurrentStep = stepNum === this.currentStep;
    const stepData = this.stepConfig[stepNum - 1];
    
    card.className = `step-card ${isCurrentStep ? 'content-card active' : 'navigation-card'}`;
    card.setAttribute('data-step', stepNum);
    
    if (isCurrentStep) {
      // Content card with form - use actual DOM elements
      const cardContent = document.createElement('div');
      cardContent.className = 'card-content';
      
      const stepContent = this.getStepContent(stepNum);
      cardContent.appendChild(stepContent);
      
      card.appendChild(cardContent);
          } else {
        // Navigation card with background image or gradient
        const imageUrl = this.getStepImageUrl(stepNum);
        const title = this.getStepTitle(stepNum);
        
        // Check if it's a gradient (CSS) or actual image URL
        const isGradient = imageUrl.startsWith('linear-gradient');
        const backgroundStyle = isGradient 
          ? `background: ${imageUrl};` 
          : `background-image: url('${imageUrl}');`;

        const isNextNavCard = stepNum === this.currentStep + 1;
        const continueHintHtml = isNextNavCard
          ? `<p class="nav-card-side-hint">${this.getNextCardContinueHint()}</p>`
          : '';
        
        card.innerHTML = `
          <div class="card-background" style="${backgroundStyle}">
            <div class="card-overlay">
              <h3 class="card-title">+ ${title}</h3>
              ${continueHintHtml}
            </div>
          </div>
        `;
        
        // If it's an image URL, add error handling
        if (!isGradient) {
          const cardBg = card.querySelector('.card-background');
          // Test if image loads
          const testImg = new Image();
          testImg.onerror = () => {
            // Silently fallback to gradient if image fails to load
            const gradient = this.getPlaceholderImage(stepNum);
            cardBg.style.background = gradient;
            cardBg.style.backgroundImage = 'none';
          };
          testImg.src = imageUrl;
        }
      }
    
    return card;
  }

  getStepImageUrl(stepNum) {
    // Get image URLs from Shopify block settings
    const imageElement = this.container.querySelector(`[data-step-${stepNum}-image]`);
    
    if (imageElement) {
      const url = imageElement.getAttribute('data-step-image-url');
      
      // More lenient validation - just check if it looks like a URL
      if (url && url.trim() !== '' && url !== 'PLACEHOLDER' && (url.includes('http') || url.includes('cdn.shopify') || url.includes('.jpg') || url.includes('.png') || url.includes('.webp'))) {
        return url;
      }
    }
    
    // Fallback to CSS gradient placeholder
    return this.getPlaceholderImage(stepNum);
  }

  getStepTitle(stepNum) {
    // Get step titles from Shopify block settings
    const imageElement = this.container.querySelector(`[data-step-${stepNum}-image]`);
    
    if (imageElement) {
      const title = imageElement.getAttribute('data-step-title');
      return title && title !== '' ? title : this.stepConfig[stepNum - 1].title;
    }
    
    return this.stepConfig[stepNum - 1].title;
  }

  getPlaceholderImage(stepNum) {
    // Use CSS gradients instead of external placeholder service
    // Return a data URL with CSS gradient
    const gradients = [
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // Pink - The Basics
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple - The Details  
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // Orange - The Looks
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Teal - The Community
      'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)', // Gold - The Interests
      'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)', // Red - The Occasion
      'linear-gradient(135deg, #48cae4 0%, #023e8a 100%)', // Blue - Emotional Growth
      'linear-gradient(135deg, #06ffa5 0%, #7209b7 100%)', // Green - Social Skills
      'linear-gradient(135deg, #ffadad 0%, #ffd6a5 100%)', // Coral - Family Life
      'linear-gradient(135deg, #caffbf 0%, #9bf6ff 100%)', // Mint - Health & Body
      'linear-gradient(135deg, #ffe8cc 0%, #ff9b71 100%)', // Peach - Exploration
      'linear-gradient(135deg, #c7ceea 0%, #b8a9c9 100%)', // Lavender - The Picture
      'linear-gradient(135deg, #a8dadc 0%, #457b9d 100%)', // Ocean - Story Theme
      'linear-gradient(135deg, #f1c0e8 0%, #cfbaf0 100%)'  // Magenta - Dedication
    ];
    
    return gradients[stepNum - 1] || gradients[1]; // Return CSS gradient
  }

  getStepContent(stepNum) {
    // Return actual DOM element, not innerHTML string!
    const formStep = this.form.querySelector(`.form-step[data-step="${stepNum}"]`);
    if (formStep) {
      // Clone the form step and preserve functionality
      const clonedStep = formStep.cloneNode(true);
      
      // Replace IDs to avoid conflicts but maintain event handling
      const elementsWithIds = clonedStep.querySelectorAll('[id]');
      elementsWithIds.forEach(element => {
        const originalId = element.id;
        element.id = `visible_${originalId}`;
        
        // Update any labels that reference this ID
        const labels = clonedStep.querySelectorAll(`label[for="${originalId}"]`);
        labels.forEach(label => {
          label.setAttribute('for', `visible_${originalId}`);
        });
      });
      
      // 🔥 CRITICAL: Add event listeners to sync changes back to hidden form
      this.setupFormElementSync(clonedStep, stepNum);
      this.addMobileStepContinueAction(clonedStep, stepNum);
      
      return clonedStep; // Return DOM element, not innerHTML!
    }
    
    // Fallback: return DOM element with text
    const fallbackDiv = document.createElement('div');
    fallbackDiv.innerHTML = `<p>Step ${stepNum} content</p>`;
    return fallbackDiv;
  }
  
  setupFormElementSync(clonedStep, stepNum) {
    // Set up real-time syncing between visible and hidden form elements
    const formElements = clonedStep.querySelectorAll('input, select, textarea');
    
    formElements.forEach(visibleElement => {
      const visibleId = visibleElement.id;
      const originalId = visibleId.replace('visible_', '');
      const hiddenElement = this.form.querySelector(`#${originalId}`);
      
      if (hiddenElement) {
        // 🔄 REAL-TIME SYNC: When user types in visible element, update hidden element
        const syncEvents = ['input', 'change', 'blur', 'keyup'];
        syncEvents.forEach(eventType => {
          visibleElement.addEventListener(eventType, () => {
            if (visibleElement.type === 'radio' || visibleElement.type === 'checkbox') {
              hiddenElement.checked = visibleElement.checked;
              if (visibleElement.checked) {
                hiddenElement.value = visibleElement.value;
              }
            } else if (visibleElement.type === 'file') {
              // For file inputs, sync the uploaded URL instead of the file object
              const uploadedUrl = visibleElement.getAttribute('data-uploaded-url');
              if (uploadedUrl) {
                // Set the URL as a value on the hidden input (it will be read from data-uploaded-url attribute)
                hiddenElement.setAttribute('data-uploaded-url', uploadedUrl);
                // console.log(`🔄 File sync: ${originalId} = "${uploadedUrl}"`);
              } else {
                // Clear the hidden file input
                hiddenElement.value = '';
                hiddenElement.removeAttribute('data-uploaded-url');
                // console.log(`🔄 File sync: ${originalId} = "cleared"`);
              }
            } else {
              hiddenElement.value = visibleElement.value;
              // console.log(`🔄 Real-time sync: ${originalId} = "${hiddenElement.value}"`);
            }
          });
        });
        
        // Also sync the initial value (except for file inputs)
        if (visibleElement.type !== 'file') {
          if (hiddenElement.value) {
            visibleElement.value = hiddenElement.value;
          }
          if (hiddenElement.checked !== undefined) {
            visibleElement.checked = hiddenElement.checked;
          }
        }
      }
    });
  }

  bindEvents() {
    // Mobile-only inline continue action at the bottom of each active step.
    this.cardsContainer.addEventListener('click', (e) => {
      const continueAction = e.target.closest('.mobile-step-continue');
      if (continueAction) {
        e.preventDefault();
        e.stopPropagation();
        this.nextStep();
      }
    });

    // Card click navigation
    this.cardsContainer.addEventListener('click', (e) => {
      const card = e.target.closest('.step-card');
      if (card && card.classList.contains('navigation-card')) {
        const targetStep = parseInt(card.getAttribute('data-step'));
        this.goToStep(targetStep);
      }
    });

    // Submit button event (only for final step)
    this.cardsContainer.addEventListener('click', (e) => {
      if (e.target.closest('.submit-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.submitForm();
      }
    });

    // Form submission prevention
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitForm();
    });

    // Handle window resize for responsive layout
    // Track window width to only re-render on actual width changes (not keyboard open/close)
    this.lastWindowWidth = window.innerWidth;
    
    window.addEventListener('resize', () => {
      // Debounce resize events
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
        const widthDiff = Math.abs(currentWidth - this.lastWindowWidth);
        
        // Only re-render if width changed significantly (> 100px)
        // This prevents re-rendering when keyboard opens/closes (height change only)
        if (widthDiff > 100) {
          console.log('🔄 Width changed significantly, re-rendering cards');
          this.lastWindowWidth = currentWidth;
          this.renderCards();
        }
      }, 300); // Increased debounce time for stability
    });
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.totalSteps || stepNumber === this.currentStep) {
      return;
    }

    const direction = stepNumber > this.currentStep ? 'forward' : 'backward';
    this.currentStep = stepNumber;

    // Simple slide animation
    this.slideToNewStep(direction);
  }

  slideToNewStep(direction) {
    // Add transition class
    this.cardsContainer.classList.add('transitioning');
    
    // Get current cards
    const currentCards = Array.from(this.cardsContainer.querySelectorAll('.step-card'));
    
    // Slide current cards out (no opacity changes to avoid flashing)
    const slideDistance = direction === 'forward' ? -120 : 120;
    currentCards.forEach((card, index) => {
      setTimeout(() => {
        card.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        card.style.transform = `translateX(${slideDistance}px)`;
      }, index * 30);
    });

    // Wait, then replace with new cards
    setTimeout(() => {
      // Clear and rebuild cards (renderCards now preserves data elements)
      this.renderCards();
      
      // Set initial state for new cards (from opposite direction)
      const newCards = Array.from(this.cardsContainer.querySelectorAll('.step-card'));
      newCards.forEach((card, index) => {
        card.style.transition = 'none';
        card.style.transform = `translateX(${-slideDistance}px)`;
        
        // Animate to final position
        setTimeout(() => {
          card.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
          card.style.transform = 'translateX(0)';
        }, index * 60 + 50);
      });
      
      // Remove transition class
      setTimeout(() => {
        this.cardsContainer.classList.remove('transitioning');
      }, 700);
    }, 250);
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.goToStep(this.currentStep + 1);
      }
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  validateCurrentStep() {
    const currentStepElement = this.form.querySelector(`.form-step[data-step="${this.currentStep}"]`);
    if (!currentStepElement) return true;

    const requiredFields = currentStepElement.querySelectorAll('[required]');
    let isValid = true;

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        field.style.borderColor = '#ef4444';
        isValid = false;
      } else {
        field.style.borderColor = '#e5e7eb';
      }
    });

    // Special validation for Step 9 (Picture Upload) - require image upload
    if (this.currentStep === 9) {
      const fileInput = currentStepElement.querySelector('input[type="file"][name="properties[child_picture]"]');
      if (fileInput) {
        const uploadedUrl = fileInput.getAttribute('data-uploaded-url');
        if (!uploadedUrl || uploadedUrl.trim() === '') {
          // Show error indicator on the file upload area
          const uploadLabel = currentStepElement.querySelector('.file-upload-label');
          if (uploadLabel) {
            uploadLabel.style.borderColor = '#ef4444';
            uploadLabel.style.backgroundColor = '#fef2f2';
          }
          isValid = false;
          
          // Show alert to user
          alert('Please upload a child picture to continue. Image upload is compulsory.');
        } else {
          // Clear error styling
          const uploadLabel = currentStepElement.querySelector('.file-upload-label');
          if (uploadLabel) {
            uploadLabel.style.borderColor = '';
            uploadLabel.style.backgroundColor = '';
          }
        }
      }
    }

    return isValid;
  }

  submitForm() {
    if (this.isSubmitting) return;

    if (!this.validateCurrentStep()) {
      return;
    }

    this.isSubmitting = true;
    
    // Show loading state
    const submitBtn = this.container.querySelector('.submit-btn span');
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Generating...';

    // ✅ REAL-TIME SYNC: With the new approach, data is synced in real-time
    // No need for complex sync since visible elements update hidden elements automatically
    // console.log('✅ Using real-time synced form data from hidden form...');

    // ✅ SIMPLIFIED DATA COLLECTION: Use FormData from hidden form (real-time synced)
    // console.log('✅ Collecting data from real-time synced hidden form...');
    const formData = new FormData(this.form);
    
    // Debug: Check if product ID input exists in the form
    const productIdInput = this.form.querySelector('input[name="id"]');
    // console.log('Product ID input element:', productIdInput);
    // console.log('Product ID input value:', productIdInput ? productIdInput.value : 'NOT FOUND');
    
    // ✅ Real-time sync should have updated all hidden form values automatically
    // console.log('✅ Form data should now be complete thanks to real-time sync!');
    
    // Manually collect ALL form fields to ensure they're captured
    const allInputs = this.form.querySelectorAll('input, select, textarea');
    // console.log('🔍 Manually checking all inputs...');
    
    allInputs.forEach(input => {
      const name = input.name;
      if (name && name.startsWith('properties[')) {
        
        if (input.type === 'radio' || input.type === 'checkbox') {
          // Skip radio and checkbox here, we'll handle them specifically below
          return;
        }
        
        // Force add text inputs, textareas, and selects
        if (input.tagName === 'INPUT' && input.type === 'text') {
          // console.log(`📝 Text input: ${name} = "${input.value}"`);
          formData.set(name, input.value || '');
        } else if (input.tagName === 'INPUT' && input.type === 'file') {
          // Handle file inputs - use uploaded URL instead of file
          const uploadedUrl = input.getAttribute('data-uploaded-url');
          if (uploadedUrl) {
            // console.log(`📁 File input: ${name} = "${uploadedUrl}"`);
            formData.set(name, uploadedUrl);
          } else {
            // console.log(`📁 File input: ${name} = "no file uploaded"`);
            formData.set(name, '');
          }
        } 
        else if (input.tagName === 'TEXTAREA') {
          // console.log(`📄 Textarea: ${name} = "${input.value}"`);
          formData.set(name, input.value || '');
        }
        else if (input.tagName === 'SELECT') {
          // console.log(`📋 Select: ${name} = "${input.value}"`);
          formData.set(name, input.value || '');
        }
      }
    });
    
    // Handle radio buttons - ensure each radio group has a value
    // console.log('🔘 Processing radio groups...');
    const radioGroups = [
      'book_format', 'child_gender', 'hair_color', 'hair_style', 'skin_tone', 'eye_color', 
      'fav_color', 'fav_sport', 'festival'
    ];
    radioGroups.forEach(groupName => {
      const selectedRadio = this.form.querySelector(`input[name="properties[${groupName}]"]:checked`);
      if (selectedRadio) {
        // console.log(`🔘 Radio ${groupName}: "${selectedRadio.value}"`);
        formData.set(`properties[${groupName}]`, selectedRadio.value);
      } else {
        // console.log(`⭕ Radio ${groupName}: NO SELECTION`);
        formData.set(`properties[${groupName}]`, '');
      }
    });
    
    // Handle checkboxes specifically
    // console.log('☑️ Processing single checkboxes...');
    const singleCheckboxes = ['wears_glasses', 'magical_adventure'];
    singleCheckboxes.forEach(checkboxName => {
      const checkbox = this.form.querySelector(`input[name="properties[${checkboxName}]"]`);
      if (checkbox) {
        const value = checkbox.checked ? checkbox.value : 'false';
        // console.log(`☑️ Checkbox ${checkboxName}: ${checkbox.checked ? 'CHECKED' : 'UNCHECKED'} = "${value}"`);
        formData.set(`properties[${checkboxName}]`, value);
      } else {
        // console.log(`❌ Checkbox ${checkboxName}: NOT FOUND`);
      }
    });
    
    // Handle multiple checkbox selections (occasions, super powers)
    // console.log('☑️ Processing checkbox groups...');
    const checkboxGroups = ['occasions', 'super_powers'];
    checkboxGroups.forEach(groupName => {
      const checkedBoxes = this.form.querySelectorAll(`input[name="properties[${groupName}]"]:checked`);
      if (checkedBoxes.length > 0) {
        const values = Array.from(checkedBoxes).map(cb => cb.value).join(', ');
        // console.log(`☑️ Checkbox group ${groupName}: ${checkedBoxes.length} selected = "${values}"`);
        formData.set(`properties[${groupName}]`, values);
      } else {
        // console.log(`⭕ Checkbox group ${groupName}: NO SELECTIONS`);
        formData.set(`properties[${groupName}]`, '');
      }
    });

    // 🔥 COMPREHENSIVE DATA COLLECTION - Force collect EVERY field
    // console.log('🔍 COMPREHENSIVE FIELD COLLECTION...');
    
    // Complete list of ALL form fields based on actual HTML structure
    const allFormFields = {
      // Text inputs
      'child_first_name': 'text',
      'child_nickname': 'text',
      'fav_animal': 'text',
      'fav_color_other': 'text',
      'fav_sport_other': 'text', 
      'fav_hobby': 'text',
      'fav_food': 'text',
      'other_occasion': 'text',
      
      // Textareas
      'biggest_dream': 'textarea',
      'child_achievements': 'textarea',
      'dedication_message': 'textarea',
      
      // Selects
      'age_group': 'select',
      'values_life_lessons': 'select',
      'social_skills': 'select', 
      'story_tone': 'select',
      
      // File inputs
      'child_picture': 'file',
      
      // Radio groups (already handled above but double-check)
      'book_format': 'radio',
      'child_gender': 'radio',
      'hair_color': 'radio',
      'hair_style': 'radio',
      'skin_tone': 'radio',
      'eye_color': 'radio',
      'fav_color': 'radio',
      'fav_sport': 'radio',
      'festival': 'radio',
      
      // Single checkboxes (already handled above but double-check)
      'wears_glasses': 'checkbox',
      'magical_adventure': 'checkbox',
      
      // Checkbox groups (already handled above but double-check)
      'super_powers': 'checkbox-group',
      'occasions': 'checkbox-group'
    };
    
    // Force collection for text/textarea/select/file fields
    Object.keys(allFormFields).forEach(fieldName => {
      const fieldType = allFormFields[fieldName];
      
      if (['text', 'textarea', 'select', 'file'].includes(fieldType)) {
        const element = this.form.querySelector(`[name="properties[${fieldName}]"]`);
        if (element) {
          let value = '';
          
          // For file inputs, use the uploaded URL instead of the file value
          if (fieldType === 'file') {
            const uploadedUrl = element.getAttribute('data-uploaded-url');
            value = uploadedUrl || '';
          } else {
            value = element.value || '';
          }
          
          formData.set(`properties[${fieldName}]`, value);
          // console.log(`📝 ${fieldType.toUpperCase()} ${fieldName}: "${value}"`);
        } else {
          console.warn(`❌ Missing ${fieldType} field: ${fieldName}`);
          formData.set(`properties[${fieldName}]`, '');
        }
      }
    });
    
    // Ensure Form Type hidden field is captured (if not already set)
    if (!formData.has('properties[Form Type]')) {
      const formTypeInput = this.form.querySelector('input[name="properties[Form Type]"]');
      if (formTypeInput) {
        formData.set('properties[Form Type]', formTypeInput.value);
        // console.log(`📝 Hidden Form Type: "${formTypeInput.value}"`);
      } else {
        console.warn('❌ Missing Form Type hidden field');
        formData.set('properties[Form Type]', 'Personalized Order Form');
      }
    }
    
    // Debug: Show field collection process
    // console.log('=== FORM FIELD COLLECTION DEBUG ===');
    // console.log('Total inputs found:', allInputs.length);
    // console.log('Properties fields found:', Array.from(allInputs).filter(input => input.name && input.name.startsWith('properties[')).length);
    
    // Complete list of ALL expected fields that should be captured
    const expectedFields = [
      'properties[Form Type]',
      'properties[book_format]', 
      'properties[child_first_name]', 
      'properties[child_nickname]', 
      'properties[child_gender]', 
      'properties[age_group]', 
      'properties[hair_color]', 
      'properties[skin_tone]', 
      'properties[eye_color]', 
      'properties[wears_glasses]', 
      'properties[fav_animal]', 
      'properties[fav_color]', 
      'properties[fav_color_other]', 
      'properties[fav_sport]', 
      'properties[fav_sport_other]', 
      'properties[fav_hobby]', 
      'properties[fav_food]', 
      'properties[super_powers]', 
      'properties[occasions]', 
      'properties[other_occasion]', 
      'properties[festival]', 
      'properties[values_life_lessons]', 
      'properties[social_skills]', 
      'properties[story_tone]', 
      'properties[biggest_dream]', 
      'properties[magical_adventure]', 
      'properties[child_achievements]', 
      'properties[child_picture]', 
      'properties[dedication_message]'
    ];
    
    // console.log('Expected fields:', expectedFields.length);
    expectedFields.forEach(fieldName => {
      const element = this.form.querySelector(`[name="${fieldName}"]`);
      if (!element) {
        console.warn(`❌ Missing field: ${fieldName}`);
      }
    });
    
    // Debug: Log all form data
    // console.log('=== COMPREHENSIVE FORM SUBMISSION DATA ===');
    let propertiesCount = 0;
    const propertiesData = {};
    
    for (let [key, value] of formData.entries()) {
      // console.log(`${key}: ${value}`);
      if (key.startsWith('properties[')) {
        propertiesCount++;
        propertiesData[key] = value;
      }
    }
    
    // console.log(`\n🎯 TOTAL PROPERTIES FIELDS IN SUBMISSION: ${propertiesCount}`);
    // console.log(`📋 Expected Fields: ${expectedFields.length}`);
    
    // Show summary of captured data by category
    const categorySummary = {
      'Text Inputs': [],
      'Dropdowns': [],
      'Radio Selections': [],
      'Single Checkboxes': [],
      'Multi-Select Tags': [],
      'Text Areas': [],
      'File Uploads': []
    };
    
    Object.keys(propertiesData).forEach(key => {
      const fieldName = key.replace('properties[', '').replace(']', '');
      const value = propertiesData[key];
      
      if (['child_first_name', 'child_nickname', 'fav_animal', 'fav_color_other', 'fav_sport_other', 'fav_hobby', 'fav_food', 'other_occasion'].includes(fieldName)) {
        categorySummary['Text Inputs'].push(`${fieldName}: "${value}"`);
      } else if (['age_group', 'values_life_lessons', 'social_skills', 'story_tone'].includes(fieldName)) {
        categorySummary['Dropdowns'].push(`${fieldName}: "${value}"`);
      } else if (['book_format', 'child_gender', 'hair_color', 'hair_style', 'skin_tone', 'eye_color', 'fav_color', 'fav_sport', 'festival'].includes(fieldName)) {
        categorySummary['Radio Selections'].push(`${fieldName}: "${value}"`);
      } else if (['wears_glasses', 'magical_adventure'].includes(fieldName)) {
        categorySummary['Single Checkboxes'].push(`${fieldName}: ${value}`);
      } else if (['super_powers', 'occasions'].includes(fieldName)) {
        categorySummary['Multi-Select Tags'].push(`${fieldName}: "${value}"`);
      } else if (['biggest_dream', 'child_achievements', 'dedication_message'].includes(fieldName)) {
        categorySummary['Text Areas'].push(`${fieldName}: "${value}"`);
      } else if (['child_picture'].includes(fieldName)) {
        categorySummary['File Uploads'].push(`${fieldName}: ${value || '[NO FILE]'}`);
      }
    });
    
    // console.log('\n📊 DATA COLLECTION SUMMARY BY CATEGORY:');
    Object.keys(categorySummary).forEach(category => {
      if (categorySummary[category].length > 0) {
        // console.log(`\n${category} (${categorySummary[category].length}):`);
        categorySummary[category].forEach(item =>  console.log(`  • ${item}`));
      }
    });
    
    // Specifically check the product ID
    const productId = formData.get('id');
    // console.log('Product ID value:', productId);
    // console.log('Product ID type:', typeof productId);
    // console.log('Product ID empty?', !productId || productId === '');
    
    if (!productId || productId === '') {
      console.error('❌ CRITICAL: No product ID found!');
      alert('Error: No product selected. Please configure a product in the form block settings in the Shopify admin.');
      submitBtn.textContent = originalText;
      this.isSubmitting = false;
      return;
    }
    
    // Validate product ID format (Shopify variant IDs are typically long numbers)
    if (!/^\d+$/.test(productId)) {
      console.error('❌ CRITICAL: Invalid product ID format:', productId);
      alert('Error: Invalid product ID format. Please check the product configuration in the form block settings.');
      submitBtn.textContent = originalText;
      this.isSubmitting = false;
      return;
    }
    
    // ✅ CRITICAL: Set isPreview to FALSE for cart submission (actual order)
    // console.log('🛒 Setting isPreview=false for cart submission (full story generation)');
    formData.set('properties[isPreview]', 'false');
    
    // Verify the isPreview value is correctly set
    // console.log('✅ Verified isPreview for cart:', formData.get('properties[isPreview]'));
    
    // Verify child_picture URL is being sent to cart
    const childPictureValue = formData.get('properties[child_picture]');
    if (childPictureValue) {
      // console.log('📸 Child picture being sent to cart:', childPictureValue);
      if (childPictureValue.startsWith('http')) {
        // console.log('✅ Valid ImgBB URL detected in cart submission');
      } else {
        console.warn('⚠️ Child picture value is not a URL:', childPictureValue);
      }
    } else {
      // console.log('ℹ️ No child picture in cart submission');
    }
    
    // console.log('=== END FORM DATA ===');
    
    // Submit to Shopify cart
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text}`);
        });
      }
      return response.json();
    })
    .then(data => {
      // console.log('Cart add successful:', data);
      // Show success message
      this.showSuccessMessage();
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('There was an error adding your order to the cart. Please try again.');
      submitBtn.textContent = originalText;
      this.isSubmitting = false;
    });
  }

  showSuccessMessage() {
    // Animate all cards out first
    const currentCards = this.cardsContainer.querySelectorAll('.step-card');
    currentCards.forEach((card, index) => {
      setTimeout(() => {
        card.style.transform = 'translateY(-30px) scale(0.8)';
        card.style.opacity = '0';
      }, index * 100);
    });

    // Show progress card after cards animate out
    setTimeout(() => {
      const progressCard = document.createElement('div');
      progressCard.className = 'step-card content-card active';
      progressCard.style.opacity = '0';
      progressCard.style.transform = 'scale(0.8)';
      progressCard.innerHTML = `
        <div class="card-content" style="text-align: center; justify-content: center; padding: 2rem;">
          <div class="progress-animation">
            <div class="progress-icon" style="font-size: 4rem; color: #FFD700; margin-bottom: 1.5rem; transform: scale(0); animation: iconPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s forwards;">✨</div>
            <h2 style="color: #B8860B; margin-bottom: 1rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.6s; font-weight: 700;">Creating Your Personalized Story</h2>
            <p id="progress-message" style="color: #6b7280; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.9s; margin-bottom: 2rem; font-size: 1.1rem;">Generating your custom preview...</p>
            
            <!-- Golden Progress Bar Container -->
            <div style="background: #f5f5f5; border-radius: 50px; height: 12px; width: 100%; margin: 2rem 0; box-shadow: inset 0 2px 4px rgba(0,0,0,0.1); opacity: 0; animation: fadeInUp 0.6s ease forwards 1.2s;">
              <div id="golden-progress-bar" style="
                height: 100%; 
                width: 0%; 
                background: linear-gradient(90deg, #FFD700 0%, #FFA500 50%, #FFD700 100%);
                border-radius: 50px;
                transition: width 0.5s ease;
                box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
                position: relative;
                overflow: hidden;
              ">
                <!-- Shimmer effect -->
                <div style="
                  position: absolute;
                  top: 0;
                  left: -100%;
                  width: 100%;
                  height: 100%;
                  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
                  animation: shimmer 2s infinite;
                "></div>
              </div>
            </div>
            
            <div id="progress-percentage" style="color: #B8860B; font-weight: 600; font-size: 1.2rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.5s;">0%</div>
            
            <div style="margin-top: 1.5rem; padding: 1rem 1.5rem; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border: 1px solid #93c5fd; border-radius: 12px; text-align: center; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.8s;">
              <div style="font-size: 1.5rem; margin-bottom: 0.5rem;">⏳</div>
              <p style="color: #1e40af; font-weight: 600; font-size: 1rem; margin: 0;">This may take a few minutes</p>
              <p style="color: #3b82f6; font-size: 0.875rem; margin: 0.25rem 0 0 0;">Please don't close this window while we craft your unique story...</p>
            </div>
          </div>
        </div>
      `;
      
      // Add CSS animations
      const style = document.createElement('style');
      style.textContent = `
        @keyframes iconPop {
          0% { transform: scale(0) rotate(0deg); }
          50% { transform: scale(1.2) rotate(180deg); }
          100% { transform: scale(1) rotate(360deg); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `;
      document.head.appendChild(style);
      
      // Replace all cards with progress card
      this.cardsContainer.innerHTML = '';
      this.cardsContainer.appendChild(progressCard);
      
      // Animate progress card in
      setTimeout(() => {
        progressCard.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        progressCard.style.opacity = '1';
        progressCard.style.transform = 'scale(1)';
      }, 50);
      
      // Start the progress animation after card is visible
      setTimeout(() => {
        this.startProgressAnimation();
      }, 1000);
    }, 600);
  }

  startProgressAnimation() {
    const progressBar = document.getElementById('golden-progress-bar');
    const progressPercentage = document.getElementById('progress-percentage');
    const progressMessage = document.getElementById('progress-message');
      // SHOPIFY-SPECIFIC API CONFIGURATION
      
      
      // Original SendGrid URL
      const externalApiUrl = 'https://hook.eu2.make.com/iwq65d37d15kq7sleeioq1o67ab5m4uu';
      
      // Use HTTPS proxy with domain (handled by Caddy)
      const vpsProxyUrl = 'https://numzaar.work.gd/api/sendgrid-proxy';
      
      // HTTPS proxy to resolve mixed content policy
      // console.log('🔒 Using HTTPS proxy URL:', vpsProxyUrl);
      // console.log('✅ Mixed content policy resolved with HTTPS');
      // console.log('🔒 Using Caddy reverse proxy with SSL/TLS');
      
      // VPS is now configured with the correct IP
      const isVpsConfigured = true;
      
      // console.log('✨ Numzaar Magical Stories - Using Windows VPS proxy server:', vpsProxyUrl);
      // console.log('⏰ VPS can handle 4-5 minute SendGrid responses without timeout');
      // console.log('🏗️ Configured for personalized story generation API calls');
    
    if (!progressBar || !progressPercentage || !progressMessage) return;
    
    // Progress messages with timing (adjusted for 4-5 minute SendGrid response)
    const progressMessages = [
      { at: 0, message: "Analyzing your child's unique details..." },
      { at: 10, message: "Selecting the perfect story theme..." },
      { at: 20, message: "Crafting personalized characters..." },
      { at: 35, message: "Writing your custom story..." },
      { at: 50, message: "Adding special touches and details..." },
      { at: 65, message: "Generating beautiful illustrations..." },
      { at: 80, message: "Finalizing your story preview..." },
      { at: 90, message: "Processing with AI (this takes 4-5 minutes)..." },
      { at: 95, message: "SendGrid processing - almost done..." }
    ];
    
      let progressPercent = 0;
      let apiRequestSent = false;
      let apiResponseReceived = false;
      
      // Store reference to this for callbacks
      const self = this;
    
    // Start API request when progress reaches 20%
    const makeApiRequest = async (retryIndex = 0) => {
      if (apiRequestSent && retryIndex === 0) return;
      if (retryIndex === 0) {
        apiRequestSent = true;
        // console.log('🚀 Starting API request for story preview...');
        progressMessage.textContent = "Generating your story preview...";
      } else {
        // console.log(`🔄 Retrying with proxy ${retryIndex + 1}/${proxyOptions.length}...`);
        progressMessage.textContent = `Trying alternative proxy (${retryIndex + 1}/${proxyOptions.length})...`;
      }
      
      // Collect form data for API request
      const apiData = this.collectFormDataForApi();
      
      // Handle different API modes
      const useSimulationMode = !isVpsConfigured;
      
      if (useSimulationMode) {
        // Simulation mode - recommended due to proxy limitations
        // console.log('🎭 Simulation mode: Generating mock response (recommended due to proxy limitations)...');
        setTimeout(() => {
          const mockResponse = {
            images: [
              'https://via.placeholder.com/400x600/FFD700/000000?text=Story+Page+1',
              'https://via.placeholder.com/400x600/FFA500/000000?text=Story+Page+2',
              'https://via.placeholder.com/400x600/FF8C00/000000?text=Story+Page+3'
            ],
            status: 'success'
          };
          // console.log('✅ Mock API response:', mockResponse);
          apiResponseReceived = true;
          this.apiResponse = mockResponse;
        }, 3000); // 3 second delay for testing
      } else {
        // Real API request via Windows VPS
        // console.log('🚀 Making real API request via VPS:', vpsProxyUrl);
        // console.log('⏰ Expected response time: 4-5 minutes (SendGrid processing time)');
        // console.log('🏗️ Note: Using Windows VPS - no timeout limitations');
        
        // Create AbortController for timeout - give more time than progress bar
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn('🚨 AbortController timeout reached after 5.5 minutes');
          controller.abort();
        }, 330000); // 5.5 minutes - longer than progress timeout
        
        // Send request to Windows VPS proxy - Force HTTP with mixed content bypass
        // Log the exact API payload before sending
        const apiPayload = {
          url: externalApiUrl,      // SendGrid URL
          data: apiData,            // Your form data
          method: 'POST'            // Method to use for SendGrid
        };
        
        // console.log('🚀 Sending API request with payload:', apiPayload);
        
        // Double-check child_picture is in the payload
        if (apiPayload.data && apiPayload.data.line_items && apiPayload.data.line_items[0]) {
          const childPicProp = apiPayload.data.line_items[0].properties.find(p => p.name === 'child_picture');
          if (childPicProp && childPicProp.value) {
            // console.log('✅✅ CONFIRMED: child_picture URL in API payload:', childPicProp.value);
          } else {
            console.error('❌❌ ERROR: child_picture NOT in API payload or empty!');
          }
        }
        
        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest', // Help bypass some security restrictions
          },
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-cache',         // Force fresh request
          redirect: 'follow',        // Follow redirects
          referrerPolicy: 'no-referrer', // Reduce referrer info
          body: JSON.stringify(apiPayload),
          signal: controller.signal // Add timeout signal
        };
        
        fetch(vpsProxyUrl, requestOptions)
        .then(response => {
          clearTimeout(timeoutId);
          // console.log('📡 API Response status:', response.status);
          // console.log('📡 API Response type:', response.type);
          
          // Check if VPS proxy response is successful
          // console.log('✅ Response received from Windows VPS proxy');
          
          if (!response.ok) {
            throw new Error(`VPS proxy error: ${response.status} ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          if (data) { // Only process if we have actual data
            // console.log('✅ API response received:', data);
            
            // Handle VPS proxy response format
            let processedData = data;
            
            if (data.success) {
              // VPS proxy successful response
              // console.log('🏗️ VPS proxy response:', data);
              // console.log('⏰ Make.com response time:', data.metadata?.durationMinutes, 'minutes');
              
              // Extract the actual Make.com response (the image array)
              processedData = data.data || data;
              // console.log('📦 Extracted data type:', Array.isArray(processedData) ? 'Array' : typeof processedData);
              // console.log('📦 Extracted data:', processedData);
              
              if (Array.isArray(processedData)) {
                // console.log('✅ Received array with', processedData.length, 'items');
                if (processedData.length > 0 && processedData[0].contentUrl) {
                  // console.log('✅ First item has contentUrl:', processedData[0].contentUrl);
                }
              }
            } else {
              // VPS proxy error response
              console.error('❌ VPS proxy reported error:', data.error);
              throw new Error(data.error || 'VPS proxy request failed');
            }
            
            apiResponseReceived = true;
            self.apiResponse = processedData;
            // console.log('💾 Stored apiResponse:', self.apiResponse);
          }
        })
        .catch(error => {
          clearTimeout(timeoutId); // Clear timeout on error
          console.error('❌ API request failed:', error);
          console.error('Error details:', error.message);
          
          // Check for mixed content or protocol issues
          if (error.message.includes('Mixed Content') || error.message.includes('blocked:mixed-content')) {
            console.error('🚨 Mixed Content Security Policy blocked the request');
            console.error('💡 Solution: The browser blocked HTTP request from HTTPS page');
            console.error('🔧 This might need HTTPS setup on the VPS proxy server');
          }
          
          if (error.message.includes('ERR_CONNECTION_TIMED_OUT') || error.message.includes('Failed to fetch')) {
            console.error('🔌 Connection timeout or network error');
            console.error('🔍 Checking if this is a protocol upgrade issue...');
            console.error('📡 Original URL attempted:', vpsProxyUrl);
          }
          
          // Additional debug info for abort errors
          if (error.name === 'AbortError') {
            console.error('🔍 AbortError details - signal aborted:', controller.signal.aborted);
            console.error('🔍 AbortError reason:', controller.signal.reason || 'No reason provided');
          }
          
          // Handle VPS proxy errors
          let errorType = 'general';
          let errorMessage = error.message;
          
          if (error.name === 'AbortError') {
            errorType = 'timeout';
            errorMessage = 'Request timed out after 5.5 minutes - this is unusual for the VPS';
            console.error('⏰ VPS request timeout - check VPS status');
          } else if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
            errorType = 'connection';
            errorMessage = 'Unable to connect to Windows VPS - check IP address and firewall';
            console.error('🚫 VPS Connection Error - verify VPS is running and accessible');
          } else if (error.message.includes('VPS proxy')) {
            errorType = 'vps_error';
            errorMessage = 'VPS proxy server error - check server logs';
            console.error('🏗️ VPS proxy error detected');
          }
          
          // Mark as complete with error
          apiResponseReceived = true;
          self.apiResponse = { 
            error: true,
            message: errorMessage,
            type: errorType,
            vpsUrl: vpsProxyUrl,
            recommendation: 'Check VPS status and ensure server is running on port 3000'
          };
          
          console.error('🔄 VPS request failed - marking API as received to complete progress bar');
        });
      }
    };
    
    // Progress animation interval
    const totalDuration = 300000; // 5 minutes in milliseconds
    const updateInterval = 100; // Update every 100ms
    const totalUpdates = totalDuration / updateInterval; // 1800 total updates
    let currentUpdate = 0;
    
    const progressInterval = setInterval(() => {
      currentUpdate++;
      
      // Calculate progress based on time OR API response
      if (!apiResponseReceived) {
        // Normal progress over 3 minutes (0% to 95%)
        const timeBasedProgress = (currentUpdate / totalUpdates) * 95; // Max 95% until API responds
        progressPercent = Math.min(timeBasedProgress, 95);
        
        // Start API request immediately on first update
        if (currentUpdate === 1 && !apiRequestSent) {
          makeApiRequest();
        }
      } else {
        // API response received, complete quickly to 100%
        if (progressPercent < 100) {
          const remainingProgress = 100 - progressPercent;
          progressPercent = Math.min(progressPercent + Math.max(remainingProgress * 0.2, 1), 100); // Faster completion
          // console.log(`🚀 Completing to 100%: ${progressPercent.toFixed(2)}%`);
        }
      }
      
      // Update progress bar
      progressBar.style.width = `${progressPercent}%`;
      progressPercentage.textContent = `${Math.round(progressPercent)}%`;
      
      // Update message based on progress
      const currentMessage = progressMessages
        .filter(msg => progressPercent >= msg.at)
        .pop();
      
      if (currentMessage && progressMessage.textContent !== currentMessage.message) {
        progressMessage.style.animation = 'pulse 0.5s ease';
        setTimeout(() => {
          progressMessage.textContent = currentMessage.message;
          progressMessage.style.animation = '';
        }, 250);
      }
      
      // Complete animation when we reach 100% and have API response
      if (Math.round(progressPercent) >= 100 && apiResponseReceived) {
        // console.log('🎯 Progress complete: 100% reached and API response received');
        clearInterval(progressInterval);
        progressMessage.textContent = "Complete! Your story preview is ready!";
        
        setTimeout(() => {
          self.showPreviewResults();
        }, 1000);
      }
      
      // Force completion if API received and we're at 99%+ (backup logic)
      if (apiResponseReceived && progressPercent >= 99) {
        // console.log('🔄 Force completing: API received and progress >= 99%');
        progressPercent = 100; // Force to exactly 100
      }
      
      // Debug logging every 50 updates
      if (currentUpdate % 50 === 0) {
        // console.log(`📊 Progress: ${Math.round(progressPercent)}% (exact: ${progressPercent.toFixed(2)}), API received: ${apiResponseReceived}, Updates: ${currentUpdate}/${totalUpdates}`);
      }
      
      // Also complete if we've run for full 3 minutes
      if (currentUpdate >= totalUpdates) {
        clearInterval(progressInterval);
        if (!apiResponseReceived) {
          console.warn('⚠️ 5-minute progress timeout reached, completing anyway...');
          apiResponseReceived = true;
          this.apiResponse = { 
            error: true, 
            message: 'Request is taking longer than expected (5+ minutes) - completing anyway',
            type: 'progress_timeout'
          };
          progressPercent = 100;
          progressBar.style.width = '100%';
          progressPercentage.textContent = '100%';
          progressMessage.textContent = "Taking longer than expected - proceeding anyway...";
          
          setTimeout(() => {
            this.showPreviewResults();
          }, 1000);
        }
      }
    }, updateInterval);
    
    // Backup timeout - complete after 5 minutes regardless (should match progress timeout)
    setTimeout(() => {
      if (!apiResponseReceived) {
        console.warn('⚠️ Backup timeout reached after 5 minutes, completing anyway...');
        apiResponseReceived = true;
        this.apiResponse = { 
          error: true, 
          message: 'Request timeout after 5 minutes',
          type: 'backup_timeout'
        };
        // Ensure progress bar completes
        const progressBar = document.getElementById('golden-progress-bar');
        const progressPercentage = document.getElementById('progress-percentage');
        if (progressBar && progressPercentage) {
          progressBar.style.width = '100%';
          progressPercentage.textContent = '100%';
        }
        this.showPreviewResults();
      }
    }, 300000); // 5 minutes
  }

  completeProgressBar() {
    // Speed up progress bar completion
    const progressBar = document.getElementById('golden-progress-bar');
    const progressPercentage = document.getElementById('progress-percentage');
    const progressMessage = document.getElementById('progress-message');
    
    if (progressBar && progressPercentage) {
      progressBar.style.width = '100%';
      progressPercentage.textContent = '100%';
      if (progressMessage) {
        progressMessage.textContent = "Complete! Your story preview is ready!";
      }
    }
  }

  collectFormDataForApi() {
    // console.log('📋 Collecting form data for API request...');
    
    // Collect all form data similar to the submission process
    const formData = new FormData(this.form);
    const properties = [];
    
    // ✅ PREVIEW API CALL: Set isPreview=true for preview generation
    // Note: For cart submission, isPreview will be set to false
    properties.push({
      name: "isPreview",
      value: true
    });
    
    // Collect all properties from form data
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const fieldName = key.replace('properties[', '').replace(']', '');
        
        // Skip file inputs here - we'll handle them specially below
        if (fieldName !== 'child_picture') {
          properties.push({
            name: fieldName,
            value: value || ""
          });
        } else {
          // console.log('⏭️ Skipping child_picture in FormData loop - will handle separately');
        }
      }
    }
    
    // Ensure all expected fields are included
    const allFormFields = [
      'Form Type', 'book_format', 'child_first_name', 'child_nickname', 'child_gender', 
      'age_group', 'hair_color', 'skin_tone', 'eye_color', 'wears_glasses', 
      'fav_animal', 'fav_color', 'fav_color_other', 'fav_sport', 'fav_sport_other', 
      'fav_hobby', 'fav_food', 'super_powers', 'occasions', 'other_occasion', 
      'festival', 'values_life_lessons', 'social_skills', 'story_tone', 
      'biggest_dream', 'magical_adventure', 'child_achievements', 'child_picture', 
      'dedication_message'
    ];
    
    allFormFields.forEach(fieldName => {
      // Check if this field is already in properties
      const existingProperty = properties.find(prop => prop.name === fieldName);
      if (!existingProperty) {
        // Get value from form
        let value = '';
        
        if (fieldName === 'Form Type') {
          value = 'Personalized Order Form';
        } else {
          const element = this.form.querySelector(`[name="properties[${fieldName}]"]`);
          if (element) {
            if (element.type === 'radio' || element.type === 'checkbox') {
              if (element.checked) {
                value = element.value;
              }
            } else if (element.type === 'file') {
              // For file inputs, use the uploaded URL from ImgBB
              const uploadedUrl = element.getAttribute('data-uploaded-url');
              value = uploadedUrl || '';
              
              // Special logging for child_picture
              if (fieldName === 'child_picture') {
                // console.log('📸 Processing child_picture field for API:');
                // console.log('  - Element found:', element);
                // console.log('  - Element ID:', element.id);
                // console.log('  - data-uploaded-url attribute:', uploadedUrl);
                // console.log('  - Final value for API:', value);
              }
            } else {
              value = element.value || '';
            }
          } else {
            // Element not found
            if (fieldName === 'child_picture') {
              console.error('❌ child_picture element NOT FOUND in form!');
            }
          }
          
          // Handle multi-select fields
          if (['super_powers', 'occasions'].includes(fieldName)) {
            const checkedBoxes = this.form.querySelectorAll(`input[name="properties[${fieldName}]"]:checked`);
            if (checkedBoxes.length > 0) {
              value = Array.from(checkedBoxes).map(cb => cb.value).join(', ');
            }
          }
        }
        
        properties.push({
          name: fieldName,
          value: value
        });
      }
    });
    
    const apiData = {
      line_items: [{
        properties: properties
      }]
    };
    
    // console.log('📤 API request data:', apiData);
    
    // Explicitly log child_picture to verify it's included
    const childPictureProperty = properties.find(prop => prop.name === 'child_picture');
    if (childPictureProperty) {
      // console.log('✅ child_picture IS included in API request:', childPictureProperty.value);
    } else {
      console.warn('⚠️ child_picture NOT found in API request properties!');
    }
    
    return apiData;
  }

  showPreviewResults() {
    const progressCard = this.cardsContainer.querySelector('.step-card');
    if (!progressCard) return;
    
    // Fade out progress content
    const progressContent = progressCard.querySelector('.progress-animation');
    if (progressContent) {
      progressContent.style.transition = 'all 0.6s ease';
      progressContent.style.opacity = '0';
      progressContent.style.transform = 'translateY(-20px)';
    }
    
    // Show preview results
    setTimeout(() => {
      if (this.apiResponse && this.apiResponse.error) {
        // Show error message
        this.showErrorMessage();
        return;
      }
      
      // Extract image URLs from API response
      const imageUrls = this.extractImageUrls(this.apiResponse);
      
      progressCard.innerHTML = `
        <div class="card-content" style="text-align: center; justify-content: flex-start; padding: 2rem; max-height: 80vh; overflow-y: auto;">
          <div class="preview-results">
            <div class="preview-header" style="margin-bottom: 2rem;">
              <div class="success-icon" style="font-size: 3rem; color: #10b981; margin-bottom: 1rem; transform: scale(0); animation: iconPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s forwards;">🎨</div>
              <h2 style="color: #059669; margin-bottom: 0.5rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.6s; font-weight: 700;">Your Story Preview is Ready!</h2>
              <p style="color: #6b7280; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.9s; font-size: 1rem; margin-bottom: 2rem;">Here's a sneak peek of your personalized story. Love what you see? Purchase the complete book!</p>
            </div>
            
            <div class="preview-gallery" style="margin-bottom: 2rem; opacity: 0; animation: fadeInUp 0.8s ease forwards 1.2s;">
              ${this.renderPreviewImages(imageUrls)}
            </div>
            
            <div class="preview-actions" style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.8s;">
              <button onclick="window.location.href='/cart'" style="
                background: linear-gradient(135deg, #059669 0%, #047857 100%);
                color: white;
                border: none;
                padding: 1rem 2rem;
                border-radius: 50px;
                font-weight: 600;
                font-size: 1.1rem;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0 6px 20px rgba(5, 150, 105, 0.3);
                text-decoration: none;
                display: inline-block;
              " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 25px rgba(5, 150, 105, 0.4)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 6px 20px rgba(5, 150, 105, 0.3)'">
                🛒 Purchase Full Book
              </button>
              
              <button onclick="window.personalizedOrderForm.restoreFormForEditing()" style="
                background: transparent;
                color: #f59e0b;
                border: 2px solid #f59e0b;
                padding: 1rem 2rem;
                border-radius: 50px;
                font-weight: 600;
                font-size: 1.1rem;
                cursor: pointer;
                transition: all 0.3s ease;
              " onmouseover="this.style.background='#f59e0b'; this.style.color='white'" onmouseout="this.style.background='transparent'; this.style.color='#f59e0b'">
                ✏️ Edit Form
              </button>
              
              <button onclick="window.personalizedOrderForm.sharePreview()" style="
                background: transparent;
                color: #059669;
                border: 2px solid #059669;
                padding: 1rem 2rem;
                border-radius: 50px;
                font-weight: 600;
                font-size: 1.1rem;
                cursor: pointer;
                transition: all 0.3s ease;
              " onmouseover="this.style.background='#059669'; this.style.color='white'" onmouseout="this.style.background='transparent'; this.style.color='#059669'">
                📤 Share Preview
              </button>
            </div>
            
            <div style="margin-top: 2rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 2.1s;">
              <p style="color: #9ca3af; font-size: 0.9rem;">
                💡 This is just a preview! The full book includes more pages, enhanced illustrations, and premium printing options.
              </p>
            </div>
          </div>
        </div>
      `;
    }, 600);
  }

  extractImageUrls(apiResponse) {
    // Extract image URLs from API response
    // console.log('🔍 API Response structure:', apiResponse);
    
    if (!apiResponse) {
      console.warn('⚠️ No API response provided');
      return [];
    }
    
    // Handle Make.com response format: array of objects with contentUrl
    // Example: [{contentUrl: 'url1'}, {contentUrl: 'url2'}, ...]
    if (Array.isArray(apiResponse)) {
      const urls = apiResponse
        .filter(item => item && item.contentUrl)
        .map(item => item.contentUrl);
      
      if (urls.length > 0) {
        // console.log('✅ Extracted', urls.length, 'image URLs from array with contentUrl');
        return urls;
      }
      
      // If array of plain strings
      const plainUrls = apiResponse.filter(item => typeof item === 'string');
      if (plainUrls.length > 0) {
        // console.log('✅ Extracted', plainUrls.length, 'plain URL strings');
        return plainUrls;
      }
    }
    
    // Handle VPS proxy wrapped response
    if (apiResponse.data) {
      // console.log('🔄 Found apiResponse.data, recursing...');
      return this.extractImageUrls(apiResponse.data);
    }
    
    // Handle { images: ["url1", "url2", "url3"] }
    if (apiResponse.images && Array.isArray(apiResponse.images)) {
      // console.log('✅ Found images array');
      return apiResponse.images;
    }
    
    // Handle { preview_images: [...] }
    if (apiResponse.preview_images && Array.isArray(apiResponse.preview_images)) {
      // console.log('✅ Found preview_images array');
      return apiResponse.preview_images;
    }
    
    // Handle { preview_urls: [...] }
    if (apiResponse.preview_urls && Array.isArray(apiResponse.preview_urls)) {
      // console.log('✅ Found preview_urls array');
      return apiResponse.preview_urls;
    }
    
    console.warn('⚠️ Could not extract image URLs from response format');
    return [];
  }

  renderPreviewImages(imageUrls) {
    if (!imageUrls || imageUrls.length === 0) {
      return `
        <div style="text-align: center; padding: 2rem; color: #6b7280;">
          <p>No preview images available at this time.</p>
        </div>
      `;
    }

    const imagesHtml = imageUrls.map((url, index) => `
      <div class="preview-image-container" style="
        display: inline-block;
        margin: 0.5rem;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        transition: all 0.3s ease;
        cursor: pointer;
      " onmouseover="this.style.transform='scale(1.05)'; this.style.boxShadow='0 12px 35px rgba(0,0,0,0.2)'" onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 8px 25px rgba(0,0,0,0.15)'">
        <img src="${url}" alt="Story Preview ${index + 1}" referrerpolicy="no-referrer" crossorigin="anonymous" style="
          max-width: 300px;
          max-height: 400px;
          width: auto;
          height: auto;
          display: block;
          object-fit: contain;
        " onclick="window.personalizedOrderForm.openImageModal('${url}')" onerror="this.parentElement.style.display='none'; console.error('Failed to load image:', '${url}')">
        <div style="padding: 0.75rem; background: #f8fafc; text-align: center;">
          <p style="margin: 0; color: #374151; font-weight: 500; font-size: 0.9rem;">Page ${index + 1}</p>
        </div>
      </div>
    `).join('');

    return `
      <div style="display: flex; flex-wrap: wrap; justify-content: center; gap: 1rem;">
        ${imagesHtml}
      </div>
    `;
  }

  openImageModal(imageUrl) {
    // Create modal for full-size image viewing
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      cursor: pointer;
    `;
    
    modal.innerHTML = `
      <img src="${imageUrl}" referrerpolicy="no-referrer" crossorigin="anonymous" style="
        max-width: 90%;
        max-height: 90%;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
      ">
      <div style="
        position: absolute;
        top: 20px;
        right: 30px;
        color: white;
        font-size: 2rem;
        cursor: pointer;
        background: rgba(0,0,0,0.5);
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      ">×</div>
    `;
    
    // Close modal on click
    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
    
    document.body.appendChild(modal);
  }

  sharePreview() {
    // Simple share functionality
    if (navigator.share) {
      navigator.share({
        title: 'My Personalized Story Preview',
        text: 'Check out the preview of my custom story!',
        url: window.location.href
      });
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Preview link copied to clipboard!');
      }).catch(() => {
        alert('Share functionality not available');
      });
    }
  }

  showErrorMessage() {
    const progressCard = this.cardsContainer.querySelector('.step-card');
    if (!progressCard) return;
    
    const errorType = this.apiResponse && this.apiResponse.type;
    let errorTitle, errorMessage;
    
    switch (errorType) {
      case 'cors':
        errorTitle = 'Connection Issue';
        errorMessage = 'Unable to connect to the preview service. This is likely a temporary connectivity issue.';
        break;
      case 'timeout':
        errorTitle = 'Request Timeout';
        errorMessage = 'The preview generation took longer than expected (3 minutes). Your order has been saved to your cart.';
        break;
      case 'endpoint_error':
        errorTitle = 'Service Temporarily Unavailable';
        errorMessage = 'The preview service is currently unavailable. Your order has been saved to your cart and you\'ll receive your preview via email.';
        break;
      default:
        errorTitle = 'Preview Generation Issue';
        errorMessage = 'We encountered an issue generating your story preview, but your order has been saved to your cart.';
    }
    
    progressCard.innerHTML = `
      <div class="card-content" style="text-align: center; justify-content: center;">
        <div class="error-animation">
          <div class="error-icon" style="font-size: 4rem; color: #ef4444; margin-bottom: 1rem; transform: scale(0); animation: iconPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s forwards;">⚠️</div>
          <h2 style="color: #dc2626; margin-bottom: 1rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.6s; font-weight: 700;">${errorTitle}</h2>
          <p style="color: #6b7280; opacity: 0; animation: fadeInUp 0.6s ease forwards 0.9s; font-size: 1.1rem;">${errorMessage}</p>
          
          ${errorType === 'cors' ? `
            <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 1rem; margin: 1.5rem 0; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.0s;">
              <p style="color: #dc2626; font-size: 0.9rem; margin: 0; font-weight: 600;">🔧 Technical Issue</p>
              <p style="color: #7f1d1d; font-size: 0.85rem; margin: 0.5rem 0 0 0;">The preview service needs to be configured to allow requests from this website. Please contact support.</p>
            </div>
          ` : ''}
          
          ${errorType === 'timeout' ? `
            <div style="background: #fef9e7; border: 1px solid #fde68a; border-radius: 8px; padding: 1rem; margin: 1.5rem 0; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.0s;">
              <p style="color: #d97706; font-size: 0.9rem; margin: 0; font-weight: 600;">⏰ Timeout</p>
              <p style="color: #92400e; font-size: 0.85rem; margin: 0.5rem 0 0 0;">The preview service is taking longer than usual. This might be due to high demand or server load.</p>
            </div>
          ` : ''}
          
          ${errorType === 'endpoint_error' ? `
            <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 1rem; margin: 1.5rem 0; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.0s;">
              <p style="color: #dc2626; font-size: 0.9rem; margin: 0; font-weight: 600;">🔗 Service Issue</p>
              <p style="color: #7f1d1d; font-size: 0.85rem; margin: 0.5rem 0 0 0;">The preview service endpoint is currently unavailable (Error 410). Please try again later or contact support.</p>
            </div>
          ` : ''}
          
          <div style="margin-top: 2rem; opacity: 0; animation: fadeInUp 0.6s ease forwards 1.2s; display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
            <button onclick="window.location.href='/cart'" style="
              background: linear-gradient(135deg, #059669 0%, #047857 100%);
              color: white;
              border: none;
              padding: 1rem 2rem;
              border-radius: 50px;
              font-weight: 600;
              font-size: 1.1rem;
              cursor: pointer;
              transition: all 0.3s ease;
              box-shadow: 0 6px 20px rgba(5, 150, 105, 0.3);
            ">
              View Cart & Continue
            </button>
            <button onclick="window.personalizedOrderForm.restoreFormForEditing()" style="
              background: transparent;
              color: #f59e0b;
              border: 2px solid #f59e0b;
              padding: 1rem 2rem;
              border-radius: 50px;
              font-weight: 600;
              font-size: 1.1rem;
              cursor: pointer;
              transition: all 0.3s ease;
            " onmouseover="this.style.background='#f59e0b'; this.style.color='white'" onmouseout="this.style.background='transparent'; this.style.color='#f59e0b'">
              ✏️ Edit Form
            </button>
            <button onclick="window.location.reload()" style="
              background: transparent;
              color: #059669;
              border: 2px solid #059669;
              padding: 1rem 2rem;
              border-radius: 50px;
              font-weight: 600;
              font-size: 1.1rem;
              cursor: pointer;
              transition: all 0.3s ease;
            " onmouseover="this.style.background='#059669'; this.style.color='white'" onmouseout="this.style.background='transparent'; this.style.color='#059669'">
              Start Over
            </button>
          </div>
        </div>
      </div>
    `;
  }

  showFinalSuccessMessage() {
    // This method is now replaced by showPreviewResults()
    // Keep for backward compatibility if needed
    this.showPreviewResults();
  }

  initializeValidation() {
    // Add real-time validation
    this.form.addEventListener('input', (e) => {
      if (e.target.hasAttribute('required')) {
        if (e.target.value.trim()) {
          e.target.style.borderColor = '#e5e7eb';
        }
      }
    });
  }

  initializeTagSelection() {
    // Handle tag selection for all tag groups
    document.addEventListener('click', (e) => {
      if (e.target.matches('.tag-label')) {
        e.preventDefault();
        
        // Find the associated input
        const label = e.target;
        const input = label.parentNode.querySelector('.tag-input');
        
        if (input) {
          // Toggle the input state
          input.checked = !input.checked;
          
          // Also sync with hidden form if this is a visible element
          const inputId = input.id;
          if (inputId && inputId.startsWith('visible_')) {
            const hiddenInputId = inputId.replace('visible_', '');
            const hiddenInput = document.getElementById(hiddenInputId);
            if (hiddenInput) {
              hiddenInput.checked = input.checked;
            }
          }
          
          // Update visual state immediately
          this.updateTagVisualState(label, input.checked);
          
          // console.log(`Tag "${input.value}" ${input.checked ? 'selected' : 'deselected'}`);
        }
      }
    });

    // Initialize visual states for any pre-selected tags
    this.updateAllTagStates();
  }

  updateTagVisualState(label, isSelected) {
    if (isSelected) {
      label.classList.add('selected');
      label.style.transform = 'translateY(-2px)';
    } else {
      label.classList.remove('selected');
      label.style.transform = 'translateY(0)';
    }
  }

  updateAllTagStates() {
    // Update visual state for all existing tags
    const allTagInputs = document.querySelectorAll('.tag-input');
    allTagInputs.forEach(input => {
      const label = input.nextElementSibling;
      if (label && label.classList.contains('tag-label')) {
        this.updateTagVisualState(label, input.checked);
      }
    });
  }

  initializeColorSelection() {
    // Handle color circle clicks directly
    document.addEventListener('click', (e) => {
      if (e.target.matches('.color-circle')) {
        e.preventDefault();
        
        // Find the associated radio button
        const label = e.target.closest('label');
        if (label) {
          const radioId = label.getAttribute('for');
          const radio = document.getElementById(radioId);
          if (radio) {
            radio.checked = true;
            
            // Trigger change event
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            
            // console.log(`Color selected: ${radio.value}`);
          }
        }
      }
    });

    // Handle color circle selection
    document.addEventListener('change', (e) => {
      if (e.target.matches('.color-radio')) {
        const selectedRadio = e.target;
        const otherInput = document.getElementById('color_other_text');
        
        if (selectedRadio.id === 'color_other') {
          // If "other" is selected, enable the text input
          if (otherInput) {
            otherInput.focus();
            otherInput.required = true;
          }
        } else {
          // If a specific color is selected, clear and disable the other input
          if (otherInput) {
            otherInput.value = '';
            otherInput.required = false;
          }
        }
      }
    });

    // Handle typing in the "other" text input
    document.addEventListener('input', (e) => {
      if (e.target.id === 'color_other_text') {
        const otherRadio = document.getElementById('color_other');
        if (otherRadio && e.target.value.trim()) {
          // Update the other radio button value with the custom color
          otherRadio.value = e.target.value.trim();
          otherRadio.checked = true;
        }
      }
    });

    // Handle "Other" label clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('.other-label')) {
        e.preventDefault();
        const otherRadio = document.getElementById('color_other');
        const otherInput = document.getElementById('color_other_text');
        
        if (otherRadio) {
          otherRadio.checked = true;
          otherRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        if (otherInput) {
          otherInput.focus();
        }
      }
    });
  }

  initializeCircleSelection() {

    // Handle ALL circle-based selections
    document.addEventListener('click', (e) => {
      let label = null;
      let radioId = null;
      

      
      // Universal circle detection - handle ALL circle types
      let clickedElement = e.target;
      
      // Handle clicks on any circle-related element
      if (clickedElement.matches('.color-circle-label, .color-circle, .festival-label, .sport-label, .format-label, .format-block-label') || 
          clickedElement.closest('.color-circle-label, .color-circle, .festival-label, .sport-label, .format-label, .format-block-label')) {
        e.preventDefault();
        
        // Find the label element
        if (clickedElement.matches('label')) {
          label = clickedElement;
        } else {
          label = clickedElement.closest('label');
        }
        
        if (label) {
          radioId = label.getAttribute('for');
          
          // Handle both original and visible_ prefixed IDs
          if (radioId && radioId.startsWith('visible_')) {
            radioId = radioId.replace('visible_', '');
          }
          
          // console.log(`🎯 Found circle selection, radioId: ${radioId}`);
        }
      }
      
      // Also handle clicks on elements INSIDE labels (circles, icons, text)
      else if (clickedElement.matches('.color-circle, .sport-circle, .festival-circle, .festival-icon, .festival-image, .format-circle, .format-icon, .format-text') ||
               clickedElement.closest('.color-circle, .sport-circle, .festival-circle, .format-circle')) {
        e.preventDefault();
        
        // Find the parent label
        label = clickedElement.closest('label');
        if (label) {
          radioId = label.getAttribute('for');
          // console.log(`🎯 Found inner circle element, radioId: ${radioId}`);
        }
      }
      
      // Handle toggle (glasses)
      else if (e.target.matches('.toggle-label, .toggle-circle') || e.target.closest('.toggle-label')) {
        e.preventDefault();
        const toggleLabel = e.target.closest('.toggle-label') || e.target;
        const checkboxId = toggleLabel.getAttribute('for');
        const checkbox = document.getElementById(checkboxId);
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          
          // FORCE VISUAL STYLING FOR TOGGLE
          const toggleCircle = toggleLabel.querySelector('.toggle-circle');
          if (toggleCircle) {
            if (checkbox.checked) {
              toggleLabel.style.backgroundColor = '#FFD700';
              toggleCircle.style.transform = 'translateX(30px)';
            } else {
              toggleLabel.style.backgroundColor = '#ccc';
              toggleCircle.style.transform = 'translateX(0px)';
            }
          }
          
          // console.log(`🔘 Toggle: ${checkboxId} = ${checkbox.checked}`);
        }
        return; // Don't process as radio
      }
      
      // If we found a valid selection, process it
      if (radioId) {
        // Try to find both visible and hidden versions of the radio button
        let radio = document.getElementById(radioId);
        let visibleRadio = document.getElementById(`visible_${radioId}`);
        
        if (!radio && !visibleRadio) {
          console.error(`❌ Radio button not found: ${radioId}`);
          return;
        }
        
        // Use whichever one exists (prefer visible for immediate feedback)
        const targetRadio = visibleRadio || radio;
        // console.log(`🎯 Setting radio ${radioId} to checked (using ${targetRadio.id})`);
        
        // Clear ALL previous selections in this radio group FIRST
        const allRadiosInGroup = document.querySelectorAll(`input[name="${targetRadio.name}"]`);
        allRadiosInGroup.forEach(radioInGroup => {
          if (radioInGroup !== targetRadio) {
            radioInGroup.checked = false;
            
            // Also clear the corresponding visible/hidden version
            const otherId = radioInGroup.id.startsWith('visible_') ? 
              radioInGroup.id.replace('visible_', '') : 
              `visible_${radioInGroup.id}`;
            const otherRadio = document.getElementById(otherId);
            if (otherRadio) otherRadio.checked = false;
          }
        });
        
        // Check both versions to ensure sync
        if (radio) radio.checked = true;
        if (visibleRadio) visibleRadio.checked = true;
          
          // Trigger change event on both versions
          if (radio) radio.dispatchEvent(new Event('change', { bubbles: true }));
          if (visibleRadio) visibleRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // console.log(`❌ No radioId found for clicked element`);
      }

      // Handle "Other" option clicks for sports
      if (e.target.matches('.other-option-label')) {
        e.preventDefault();
        const otherRadio = document.getElementById('sport_other');
        const otherInput = document.getElementById('sport_other_text');
        
        if (otherRadio) {
          otherRadio.checked = true;
          otherRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        if (otherInput) {
          otherInput.focus();
        }
      }
    });

    // Handle changes to sport "other" input
    document.addEventListener('change', (e) => {
      if (e.target.id === 'sport_other') {
        const otherInput = document.getElementById('sport_other_text');
        if (otherInput) {
          otherInput.focus();
        }
      }
    });

    // Handle typing in sport "other" input
    document.addEventListener('input', (e) => {
      if (e.target.id === 'sport_other_text') {
        const otherRadio = document.getElementById('sport_other');
        if (otherRadio && e.target.value.trim()) {
          otherRadio.value = e.target.value.trim();
          otherRadio.checked = true;
        }
      }
    });
  }

  initializeCharacterCount() {
    // Handle character count for textareas with limits
    document.addEventListener('input', (e) => {
      // Handle biggest dream
      if (e.target.id === 'biggest_dream' || e.target.id === 'visible_biggest_dream') {
        const textarea = e.target;
        // Try both original and visible_ prefixed counter IDs
        const counter = document.getElementById('dream-count') || document.getElementById('visible_dream-count');
        
        // Enforce 100 character limit
        if (textarea.value.length > 100) {
          textarea.value = textarea.value.substring(0, 100);
        }
        
        if (counter) {
          const currentLength = textarea.value.length;
          counter.textContent = currentLength;
          
          // Change color if approaching limit (100 char limit)
          if (currentLength > 80) {
            counter.style.color = '#e74c3c';
          } else if (currentLength > 60) {
            counter.style.color = '#f39c12';
          } else {
            counter.style.color = '#666';
          }
        } else {
          console.warn('Could not find dream-count or visible_dream-count element');
        }
      }
      
      // Handle child achievements
      if (e.target.id === 'child_achievements' || e.target.id === 'visible_child_achievements') {
        const textarea = e.target;
        // Try both original and visible_ prefixed counter IDs
        const counter = document.getElementById('achievements-count') || document.getElementById('visible_achievements-count');
        
        // Enforce 500 character limit
        if (textarea.value.length > 500) {
          textarea.value = textarea.value.substring(0, 500);
        }
        
        if (counter) {
          const currentLength = textarea.value.length;
          counter.textContent = currentLength;
          
          // Change color if approaching limit (500 char limit)
          if (currentLength > 450) {
            counter.style.color = '#e74c3c';
          } else if (currentLength > 400) {
            counter.style.color = '#f39c12';
          } else {
            counter.style.color = '#666';
          }
        } else {
          console.warn('Could not find achievements-count or visible_achievements-count element');
        }
      }
      
      // Handle dedication message
      if (e.target.id === 'dedication_message' || e.target.id === 'visible_dedication_message') {
        const textarea = e.target;
        // Try both original and visible_ prefixed counter IDs
        const counter = document.getElementById('dedication-count') || document.getElementById('visible_dedication-count');
        
        // Enforce 500 character limit
        if (textarea.value.length > 500) {
          textarea.value = textarea.value.substring(0, 500);
        }
        
        if (counter) {
          const currentLength = textarea.value.length;
          counter.textContent = currentLength;
          
          // Change color if approaching limit (500 char limit)
          if (currentLength > 450) {
            counter.style.color = '#e74c3c';
          } else if (currentLength > 400) {
            counter.style.color = '#f39c12';
          } else {
            counter.style.color = '#666';
          }
        } else {
          console.warn('Could not find dedication-count or visible_dedication-count element');
        }
      }
    });
  }

  syncAllFormValues() {
    // console.log('🔄 Syncing all form values from visible cards to hidden form...');
    
    // Find all visible form elements in the current cards
    const visibleElements = this.cardsContainer.querySelectorAll('input, select, textarea');
    let syncCount = 0;
    
    // console.log(`Found ${visibleElements.length} visible elements to sync`);
    
    visibleElements.forEach(visibleElement => {
      const name = visibleElement.name;
      const id = visibleElement.id;
      if (!name || !id) return;
      
      // Handle elements with visible_ prefix
      let targetId = id;
      if (id.startsWith('visible_')) {
        targetId = id.replace('visible_', '');
      }
      
      // Find corresponding element in hidden form by ID first, then by name
      let hiddenElement = this.form.querySelector(`#${targetId}`);
      if (!hiddenElement) {
        hiddenElement = this.form.querySelector(`[name="${name}"]`);
      }
      
      if (!hiddenElement) {
        console.warn(`⚠️ No hidden element found for: ${name} (ID: ${id})`);
        return;
      }
      
      let valueChanged = false;
      
      if (visibleElement.type === 'radio') {
        // For radio buttons, sync checked state
        if (visibleElement.checked !== hiddenElement.checked) {
          hiddenElement.checked = visibleElement.checked;
          valueChanged = true;
          // console.log(`🔄 Synced radio ${name}: ${visibleElement.checked ? 'CHECKED' : 'UNCHECKED'} (${visibleElement.value})`);
        }
      } else if (visibleElement.type === 'checkbox') {
        // For checkboxes, sync checked state
        if (visibleElement.checked !== hiddenElement.checked) {
          hiddenElement.checked = visibleElement.checked;
          valueChanged = true;
          // console.log(`🔄 Synced checkbox ${name}: ${visibleElement.checked ? 'CHECKED' : 'UNCHECKED'} (${visibleElement.value})`);
        }
      } else {
        // For text, textarea, select, file - sync the value
        if (visibleElement.value !== hiddenElement.value) {
          hiddenElement.value = visibleElement.value;
          valueChanged = true;
          // console.log(`🔄 Synced ${visibleElement.tagName.toLowerCase()} ${name}: "${visibleElement.value}"`);
        }
      }
      
      if (valueChanged) syncCount++;
    });
    
    // ADDITIONAL: Manually sync specific problematic fields by checking their visible counterparts
    // console.log('🔧 Manual sync for critical fields...');
    
    const criticalFields = [
      'child_first_name', 'child_nickname', 'age_group',
      'fav_animal', 'fav_hobby', 'fav_food', 'biggest_dream', 'child_achievements', 
      'dedication_message', 'other_occasion', 'fav_color_other', 'fav_sport_other'
    ];
    
    criticalFields.forEach(fieldName => {
      const visibleField = this.cardsContainer.querySelector(`#visible_${fieldName}, #${fieldName}`);
      const hiddenField = this.form.querySelector(`[name="properties[${fieldName}]"]`);
      
      if (visibleField && hiddenField && visibleField.value !== hiddenField.value) {
        hiddenField.value = visibleField.value;
        syncCount++;
        // console.log(`🔧 Manual sync ${fieldName}: "${visibleField.value}"`);
      }
    });
    
    // console.log(`🔄 Form sync complete! ${syncCount} values updated.`);
  }

  setupVariantSelection() {
    // console.log('🎯 Setting up variant selection based on book format...');
    
    // Get variant ID inputs
    const variantIdInput = this.form.querySelector('#variant-id-input');
    const pdfVariantInput = this.form.querySelector('#pdf-variant-id');
    const physicalVariantInput = this.form.querySelector('#physical-variant-id');
    const paperbackVariantInput = this.form.querySelector('#paperback-variant-id');
    
    if (!variantIdInput || !pdfVariantInput || !physicalVariantInput || !paperbackVariantInput) {
      console.warn('⚠️ Variant inputs not found, skipping variant selection setup');
      return;
    }
    
    // Get the variant IDs
    const pdfVariantId = pdfVariantInput.value;
    const physicalVariantId = physicalVariantInput.value;
    const paperbackVariantId = paperbackVariantInput.value;
    
    // console.log('📖 PDF Variant ID:', pdfVariantId);
    // console.log('📚 Physical Variant ID:', physicalVariantId);
    
    // Function to update variant based on selection
    const updateVariant = () => {
      const bookFormatRadios = this.form.querySelectorAll('input[name="properties[book_format]"]');
      let selectedFormat = null;
      
      // Check both visible and hidden forms for the selected format
      bookFormatRadios.forEach(radio => {
        if (radio.checked) {
          selectedFormat = radio.value;
        }
      });
      
      // console.log('📋 Selected book format:', selectedFormat);
      
      if (selectedFormat === 'PDF') {
        variantIdInput.value = pdfVariantId;
        // console.log('✅ Set variant to PDF:', pdfVariantId);
      } else if (selectedFormat === 'Physical Book') {
        variantIdInput.value = physicalVariantId;
        // console.log('✅ Set variant to Physical Book:', physicalVariantId);
      } else if (selectedFormat === 'Paper Back') {
        variantIdInput.value = paperbackVariantId;
        // console.log('Set variant to Paper Back:', paperbackVariantId);
      } else {
        // Default to PDF variant if nothing selected
        variantIdInput.value = pdfVariantId;
        // console.log('⚠️ No format selected, defaulting to PDF variant:', pdfVariantId);
      }
    };
    
    // Listen for changes to book format selection
    const bookFormatRadios = this.form.querySelectorAll('input[name="properties[book_format]"]');
    bookFormatRadios.forEach(radio => {
      radio.addEventListener('change', updateVariant);
    });
    
    // Also listen for clicks on the format block labels (for both visible and hidden forms)
    document.addEventListener('click', (e) => {
      const formatLabel = e.target.closest('.format-block-label');
      if (formatLabel) {
        // Small delay to ensure the radio button state has updated
        setTimeout(updateVariant, 50);
      }
    });
    
    // Set initial variant
    updateVariant();
  }

  setupInnerStrengthsLimit() {
    // Use alert to ensure visibility
    console.warn('INNER STRENGTHS LIMIT: Starting setup...');
    
    // Simple, direct approach - check periodically and enforce limit
    let limitCheckInterval;
    let lastCheckedCount = -1; // Track changes to reduce spam
    
    const startLimitEnforcement = () => {
      console.warn('INNER STRENGTHS LIMIT: Enforcement started!');
      
      // Clear any existing interval
      if (limitCheckInterval) {
        clearInterval(limitCheckInterval);
      }
      
      // Check every 200ms for changes
      limitCheckInterval = setInterval(() => {
        const allPowerCheckboxes = document.querySelectorAll('input[name="properties[super_powers]"]');
        
        if (allPowerCheckboxes.length > 0) {
          const checkedBoxes = Array.from(allPowerCheckboxes).filter(cb => cb.checked);
          
          // Remove duplicates by value (since we have visible + hidden pairs)
          const uniqueValues = [];
          const uniqueCheckedBoxes = [];
          
          checkedBoxes.forEach(checkbox => {
            if (!uniqueValues.includes(checkbox.value)) {
              uniqueValues.push(checkbox.value);
              uniqueCheckedBoxes.push(checkbox);
            }
          });
          
          const checkedCount = uniqueValues.length;
          
          // Only log when count changes
          if (checkedCount !== lastCheckedCount) {
            console.warn(`INNER STRENGTHS: ${checkedCount}/3 selected (${checkedBoxes.length} total checkboxes, ${uniqueValues.length} unique)`);
            console.warn('Selected powers:', uniqueValues);
            lastCheckedCount = checkedCount;
          }
          
          // If more than 3 unique values are checked, uncheck the extras
          if (checkedCount > 3) {
            console.error('TOO MANY UNIQUE SELECTED! Fixing now...');
            
            // Keep only first 3 unique values
            const allowedValues = uniqueValues.slice(0, 3);
            console.error('Keeping:', allowedValues);
            
            // Uncheck all checkboxes with values not in the allowed list
            allPowerCheckboxes.forEach(checkbox => {
              if (checkbox.checked && !allowedValues.includes(checkbox.value)) {
                checkbox.checked = false;
                console.error('Unchecked:', checkbox.value, checkbox.id);
              }
            });
          }
          
          // Visual feedback - disable options when at limit
          if (checkedCount === 3) {
            // When exactly 3 are selected, disable the unselected ones
            allPowerCheckboxes.forEach(checkbox => {
              if (!checkbox.checked) {
                const label = document.querySelector(`label[for="${checkbox.id}"]`);
                if (label) {
                  label.style.opacity = '0.5';
                  label.style.pointerEvents = 'none';
                }
              }
            });
          } else {
            // When under 3, enable all options
            allPowerCheckboxes.forEach(checkbox => {
              const label = document.querySelector(`label[for="${checkbox.id}"]`);
              if (label) {
                label.style.opacity = '1';
                label.style.pointerEvents = 'auto';
              }
            });
          }
        }
      }, 200);
    };
    
    // Start immediately if checkboxes exist, otherwise wait
    const powerCheckboxes = document.querySelectorAll('input[name="properties[super_powers]"]');
    if (powerCheckboxes.length > 0) {
      console.warn(`FOUND ${powerCheckboxes.length} super powers checkboxes immediately`);
      startLimitEnforcement();
    } else {
      console.warn('No checkboxes found yet, waiting...');
      // Wait and try again
      setTimeout(() => {
        const delayedCheckboxes = document.querySelectorAll('input[name="properties[super_powers]"]');
        if (delayedCheckboxes.length > 0) {
          console.warn(`FOUND ${delayedCheckboxes.length} super powers checkboxes after delay`);
          startLimitEnforcement();
        } else {
          console.error('NO CHECKBOXES FOUND AFTER DELAY!');
        }
      }, 2000);
    }
  }

  initializeFileUpload() {
    // Handle file input changes with preview
    document.addEventListener('change', (e) => {
      if (e.target.type === 'file' && e.target.name.includes('child_picture')) {
        this.handleFileUpload(e.target);
      }
    });

    // Handle drag and drop
    document.addEventListener('dragover', (e) => {
      if (e.target.closest('.file-upload-label')) {
        e.preventDefault();
        e.target.closest('.file-upload-label').style.backgroundColor = '#e8f5e8';
      }
    });

    document.addEventListener('dragleave', (e) => {
      if (e.target.closest('.file-upload-label')) {
        e.target.closest('.file-upload-label').style.backgroundColor = '#fafafa';
      }
    });

    document.addEventListener('drop', (e) => {
      const uploadLabel = e.target.closest('.file-upload-label');
      if (uploadLabel) {
        e.preventDefault();
        uploadLabel.style.backgroundColor = '#fafafa';
        
        const fileInput = uploadLabel.parentNode.querySelector('.file-input');
        if (e.dataTransfer.files.length > 0) {
          // Create new file list
          const dt = new DataTransfer();
          dt.items.add(e.dataTransfer.files[0]);
          fileInput.files = dt.files;
          
          // Trigger change event
          this.handleFileUpload(fileInput);
        }
      }
    });

    // Handle remove file button
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('remove-file')) {
        e.preventDefault();
        this.removeFile(e.target);
      }
    });
  }

  initializeAutoSave() {
    // Auto-save form data to localStorage on any input change
    // console.log('🔄 Initializing auto-save...');
    
    // Debounced save function to avoid excessive localStorage writes
    let saveTimeout;
    const debouncedSave = () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        this.saveFormData();
      }, 500); // Save 500ms after user stops typing
    };
    
    // Listen for all form input changes
    this.form.addEventListener('input', debouncedSave);
    this.form.addEventListener('change', debouncedSave);
  }

  saveFormData() {
    // console.log('💾 Saving form data to localStorage...');
    
    // Show saving indicator
    this.showSaveIndicator('saving');
    
    const formData = {};
    
    // Save all text inputs, textareas, and selects
    const textInputs = this.form.querySelectorAll('input[type="text"], input[type="email"], textarea, select');
    textInputs.forEach(input => {
      if (input.name) {
        formData[input.name] = input.value;
      }
    });
    
    // Save radio button selections
    const radioGroups = {};
    const radioInputs = this.form.querySelectorAll('input[type="radio"]:checked');
    radioInputs.forEach(radio => {
      if (radio.name) {
        radioGroups[radio.name] = radio.value;
      }
    });
    formData.radioSelections = radioGroups;
    
    // Save checkbox selections
    const checkboxes = {};
    const checkboxInputs = this.form.querySelectorAll('input[type="checkbox"]');
    checkboxInputs.forEach(checkbox => {
      if (checkbox.name) {
        if (!checkboxes[checkbox.name]) {
          checkboxes[checkbox.name] = [];
        }
        if (checkbox.checked) {
          checkboxes[checkbox.name].push(checkbox.value);
        }
      }
    });
    formData.checkboxSelections = checkboxes;
    
    // Save file upload URL (not the file itself)
    const fileInput = this.form.querySelector('input[type="file"]');
    if (fileInput) {
      const uploadedUrl = fileInput.getAttribute('data-uploaded-url');
      if (uploadedUrl) {
        formData.uploadedImageUrl = uploadedUrl;
      }
    }
    
    // Save current step
    formData.currentStep = this.currentStep;
    
    // Save timestamp
    formData.savedAt = new Date().toISOString();
    
    // Save to localStorage
    try {
      localStorage.setItem('personalizedOrderFormData', JSON.stringify(formData));
      // console.log('✅ Form data saved successfully');
      
      // Show saved indicator
      this.showSaveIndicator('saved');
    } catch (error) {
      console.error('❌ Error saving form data:', error);
      this.showSaveIndicator('error');
    }
  }

  showSaveIndicator(status) {
    const indicator = document.getElementById('autoSaveIndicator');
    if (!indicator) return;
    
    const iconSpan = indicator.querySelector('.indicator-icon');
    const textSpan = indicator.querySelector('.indicator-text');
    
    if (status === 'saving') {
      iconSpan.textContent = '💾';
      textSpan.textContent = 'Saving...';
      indicator.classList.add('saving');
      indicator.classList.remove('visible');
      // Small delay before showing to avoid flashing
      setTimeout(() => {
        indicator.classList.add('visible');
      }, 50);
    } else if (status === 'saved') {
      iconSpan.textContent = '✅';
      textSpan.textContent = 'Saved';
      indicator.classList.remove('saving');
      indicator.classList.add('visible');
      
      // Hide after 2 seconds
      setTimeout(() => {
        indicator.classList.remove('visible');
      }, 2000);
    } else if (status === 'error') {
      iconSpan.textContent = '❌';
      textSpan.textContent = 'Save failed';
      indicator.classList.remove('saving');
      indicator.classList.add('visible');
      
      // Hide after 3 seconds
      setTimeout(() => {
        indicator.classList.remove('visible');
      }, 3000);
    }
  }

  loadSavedFormData() {
    // console.log('📂 Loading saved form data from localStorage...');
    
    try {
      const savedData = localStorage.getItem('personalizedOrderFormData');
      if (!savedData) {
        // console.log('ℹ️ No saved form data found');
        return;
      }
      
      const formData = JSON.parse(savedData);
      // console.log('📋 Loaded form data:', formData);
      
      // Show notification about saved data
      const savedDate = formData.savedAt ? new Date(formData.savedAt) : null;
      const timeAgo = savedDate ? this.getTimeAgo(savedDate) : 'recently';
      
      // Ask user if they want to restore
      const shouldRestore = confirm(`📝 Found saved form data from ${timeAgo}.\n\nWould you like to continue where you left off?\n\n✅ Click OK to restore your saved data\n❌ Click Cancel to start fresh`);
      
      if (!shouldRestore) {
        // User wants to start fresh
        this.clearSavedFormData();
        return;
      }
      
      // Restore text inputs, textareas, and selects
      Object.keys(formData).forEach(key => {
        if (key === 'radioSelections' || key === 'checkboxSelections' || key === 'currentStep' || key === 'uploadedImageUrl' || key === 'savedAt') {
          return; // Skip these, handle separately
        }
        
        const input = this.form.querySelector(`[name="${key}"]`);
        if (input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA' || input.tagName === 'SELECT')) {
          input.value = formData[key];
        }
      });
      
      // Restore radio button selections
      if (formData.radioSelections) {
        Object.keys(formData.radioSelections).forEach(name => {
          const value = formData.radioSelections[name];
          const radio = this.form.querySelector(`input[name="${name}"][value="${value}"]`);
          if (radio) {
            radio.checked = true;
          }
        });
      }
      
      // Restore checkbox selections
      if (formData.checkboxSelections) {
        Object.keys(formData.checkboxSelections).forEach(name => {
          const values = formData.checkboxSelections[name];
          // First uncheck all
          const allCheckboxes = this.form.querySelectorAll(`input[name="${name}"]`);
          allCheckboxes.forEach(cb => cb.checked = false);
          
          // Then check the saved ones
          values.forEach(value => {
            const checkbox = this.form.querySelector(`input[name="${name}"][value="${value}"]`);
            if (checkbox) {
              checkbox.checked = true;
            }
          });
        });
      }
      
      // Restore uploaded image
      if (formData.uploadedImageUrl) {
        const fileInput = this.form.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.setAttribute('data-uploaded-url', formData.uploadedImageUrl);
          
          // Show preview
          const container = fileInput.closest('.file-upload-container');
          if (container) {
            const preview = container.querySelector('.file-preview');
            const previewImg = container.querySelector('.preview-image');
            const uploadLabel = container.querySelector('.file-upload-label');
            
            if (preview && previewImg && uploadLabel) {
              previewImg.src = formData.uploadedImageUrl;
              preview.style.display = 'inline-block';
              uploadLabel.style.display = 'none';
            }
          }
          
          this.uploadedImageUrl = formData.uploadedImageUrl;
        }
      }
      
      // Restore current step
      if (formData.currentStep && formData.currentStep !== this.currentStep) {
        this.currentStep = formData.currentStep;
        this.renderCards();
      }
      
      // console.log('✅ Form data loaded successfully');
      
      // Show success message
      this.showSaveIndicator('saved');
      
      // Update visual states
      setTimeout(() => {
        this.updateAllTagStates();
      }, 200);
      
    } catch (error) {
      console.error('❌ Error loading form data:', error);
    }
  }

  getTimeAgo(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  }

  clearSavedFormData() {
    // console.log('🗑️ Clearing saved form data...');
    try {
      localStorage.removeItem('personalizedOrderFormData');
      // console.log('✅ Saved form data cleared');
    } catch (error) {
      console.error('❌ Error clearing saved data:', error);
    }
  }

  restoreFormForEditing() {
    // console.log('✏️ Restoring form for editing...');
    
    // Reset to step 1
    this.currentStep = 1;
    this.isSubmitting = false;
    
    // Clear the cards container and re-render
    this.renderCards();
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // console.log('✅ Form restored for editing');
  }

  async handleFileUpload(fileInput) {
    const file = fileInput.files[0];
    const container = fileInput.closest('.file-upload-container');
    const preview = container.querySelector('.file-preview');
    const previewImg = container.querySelector('.preview-image');
    const uploadLabel = container.querySelector('.file-upload-label');
    const uploadStatus = container.querySelector('.upload-status');

    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPG, etc.)');
        fileInput.value = '';
        return;
      }

      // Validate file size (10MB max for ImgBB free tier is 32MB, but we'll keep 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        fileInput.value = '';
        return;
      }

      // Show loading state
      uploadLabel.textContent = 'Uploading...';
      uploadLabel.style.display = 'block';

      try {
        // Upload to ImgBB with 1 hour expiration (3600 seconds)
        const formData = new FormData();
        formData.append('image', file);
        formData.append('expiration', '3600'); // Auto-delete after 1 hour
        
        const response = await fetch(`https://api.imgbb.com/1/upload?key=${this.imgbbApiKey}`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();
        
        if (data.success) {
          // Store the uploaded image URL
          this.uploadedImageUrl = data.data.url;
          
          // Display preview using the uploaded image URL
          previewImg.src = data.data.url;
          preview.style.display = 'inline-block';
          uploadLabel.style.display = 'none';
          
          // Show upload success status
          if (uploadStatus) {
            uploadStatus.style.display = 'block';
          }
          
          // console.log(`📸 Image uploaded to ImgBB: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
          // console.log(`🔗 Image URL: ${data.data.url}`);
          // console.log(`⏰ Image will auto-delete in 1 hour`);

          // Store the URL in the data attribute on the file input
          fileInput.setAttribute('data-uploaded-url', data.data.url);
          // console.log(`✅ Stored URL in data-uploaded-url attribute`);

          // Sync the uploaded URL to the hidden file input as well (if it exists)
          const originalId = fileInput.id.replace('visible_', '');
          const hiddenInput = document.getElementById(originalId);
          if (hiddenInput && hiddenInput !== fileInput) {
            hiddenInput.setAttribute('data-uploaded-url', data.data.url);
            // console.log(`🔄 Synced URL to hidden input: ${data.data.url}`);
          }
        } else {
          throw new Error(data.error?.message || 'Upload failed');
        }
      } catch (error) {
        console.error('ImgBB upload error:', error);
        alert('Failed to upload image. Please try again.');
        uploadLabel.textContent = '📸 Click to upload or drag & drop';
        uploadLabel.style.display = 'block';
        if (uploadStatus) {
          uploadStatus.style.display = 'none';
        }
        fileInput.value = '';
      }
    }
  }

  removeFile(removeButton) {
    const container = removeButton.closest('.file-upload-container');
    const fileInput = container.querySelector('.file-input');
    const preview = container.querySelector('.file-preview');
    const uploadLabel = container.querySelector('.file-upload-label');
    const uploadStatus = container.querySelector('.upload-status');

    // Clear file input
    fileInput.value = '';
    
    // Clear uploaded URL
    this.uploadedImageUrl = null;
    fileInput.removeAttribute('data-uploaded-url');
    
    // Clear hidden form sync
    const originalId = fileInput.id.replace('visible_', '');
    const hiddenInput = document.getElementById(originalId);
    if (hiddenInput && hiddenInput !== fileInput) {
      hiddenInput.value = '';
      hiddenInput.removeAttribute('data-uploaded-url');
    }

    // Hide preview, show upload area
    preview.style.display = 'none';
    uploadLabel.style.display = 'block';
    uploadLabel.textContent = '📸 Click to upload or drag & drop';
    
    // Hide upload status
    if (uploadStatus) {
      uploadStatus.style.display = 'none';
    }
    
    // console.log('📸 Image removed');
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const forms = document.querySelectorAll('.multi-step-form');
  forms.forEach(form => {
    new PersonalizedOrderForm(form);
  });
});
